import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  TextField,
} from '@mui/material'
import React, { useState } from 'react'
import { Column } from 'shared/ui/Table/types'
import { Table } from 'shared/ui/Table/Table'
import {
  useAttributesForTopLevelKey,
  useSetAttributeForTopLevelKey,
  useSortedAttributes,
  useSetSortOrder,
  useHasChanges,
} from './hooks'
import { DataPreview } from './DataPreview'
import { SortableList } from '../../components/utility/DragAndDrop'
import { ConfirmWithDialogButton } from 'shared/ui'
import { ChangeableContent } from './utility'
import { AttributeItem } from './types'

export function Attributes({ configKey }: { configKey: string }) {
  const tableColumns = useTableColumns(configKey)
  const sortedKeys = useSortedAttributes(configKey)

  if (!sortedKeys) return null

  return (
    <Accordion>
      <AccordionSummary>
        <Box
          width="100%"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          {sortedKeys.length} Attributes
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <EditSortOrder configKey={configKey} />
        <Table<string>
          overrideHeight={275}
          columns={tableColumns}
          items={sortedKeys}
        />
      </AccordionDetails>
    </Accordion>
  )
}

function useTableColumns(configKey: string): Column<string>[] {
  const [editing, setEditing] = useState('')
  const attributes = useAttributesForTopLevelKey(configKey) ?? {}
  const setAttr = useSetAttributeForTopLevelKey(configKey)
  const { attrHasChanges } = useHasChanges()

  return [
    {
      title: 'Key',
      rowContent(param: string) {
        const changed = attrHasChanges(configKey, param)
        return <ChangeableContent changed={changed}>{param}</ChangeableContent>
      },
    },
    {
      title: 'Value Type',
      rowContent(param: string) {
        return attributes[param]?.valueType
      },
    },
    {
      title: 'Is Metadata',
      rowContent(param: string) {
        return (
          <Checkbox
            disabled={editing !== param}
            checked={attributes[param]?.isMetadata}
            onChange={() =>
              setAttr({
                attrKey: param,
                attrProp: 'isMetadata',
                newValue: !attributes[param].isMetadata,
              })
            }
          />
        )
      },
    },
    {
      title: 'Student Facing',
      rowContent(param: string) {
        const isString = attributes[param]?.valueType === 'string'
        if (!isString) return null
        return (
          <Checkbox
            disabled={editing !== param}
            checked={attributes[param].isStudentFacing}
            onChange={() =>
              setAttr({
                attrKey: param,
                attrProp: 'isStudentFacing',
                newValue: !attributes[param].isStudentFacing,
              })
            }
          />
        )
      },
    },
    {
      title: 'Example',
      rowContent(param: string) {
        return <DataPreview configKey={configKey} attributeKey={param} />
      },
    },
    {
      title: 'Custom Title',
      rowContent(param: string) {
        if (param === editing) {
          return <EditCustomTitle configKey={configKey} attrKey={param} />
        }
        return attributes[param]?.customTitle
      },
    },
    {
      title: 'List Key',
      rowContent(param) {
        if (attributes[param]?.valueType !== 'array') {
          return null
        }
        if (param === editing) {
          return (
            <EditAttrStringProp
              configKey={configKey}
              attrKey={param}
              stringProp={'listKey'}
            />
          )
        }
        return attributes[param]?.listKey
      },
    },
    {
      title: 'Edit',
      rowContent(param: string) {
        return (
          <Button onClick={() => setEditing(param === editing ? '' : param)}>
            Edit
          </Button>
        )
      },
    },
  ]
}

function EditCustomTitle({
  configKey,
  attrKey,
}: {
  configKey: string
  attrKey: string
}) {
  return (
    <EditAttrStringProp
      configKey={configKey}
      attrKey={attrKey}
      stringProp={'customTitle'}
    />
  )
}

function EditAttrStringProp({
  configKey,
  attrKey,
  stringProp,
}: {
  configKey: string
  attrKey: string
  stringProp: keyof AttributeItem
}) {
  const attributes = useAttributesForTopLevelKey(configKey) ?? {}
  const currentValue = attributes[attrKey][stringProp]
  const [text, setText] = useState(currentValue ?? '')
  const setAttribute = useSetAttributeForTopLevelKey(configKey)

  const onBlur = () => {
    setAttribute({ attrKey, attrProp: stringProp, newValue: text })
  }

  return (
    <TextField
      value={text}
      onBlur={onBlur}
      onChange={({ target }) => {
        setText(target.value)
      }}
    />
  )
}

function EditSortOrder({ configKey }: { configKey: string }) {
  const sortedKeys = useSortedAttributes(configKey)
  const setSortOrder = useSetSortOrder(configKey)
  const [currentSortOrder, setCurrentOrder] = useState<string[]>(
    sortedKeys ?? [],
  )

  function SortingList() {
    return (
      <SortableList
        items={currentSortOrder}
        onOrderChange={(o) => setCurrentOrder(o as string[])}
        renderItem={(i) => i}
      />
    )
  }

  return (
    <ConfirmWithDialogButton
      dialogTitle={'Sort Child Items'}
      dialogContent={<SortingList />}
      onClick={() => setSortOrder(currentSortOrder)}
      cta="Set"
      cancel="Cancel"
    >
      Edit Order
    </ConfirmWithDialogButton>
  )
}
